































































































































































































































import {
  defineComponent,
  ref,
  PropType,
  computed,
  watch,
} from '@vue/composition-api';
import {
  DiscourseUnit,
  Word,
  LocaleCode,
  DiscourseDisplayUnit,
} from '@oare/types';
import { DiscourseHtmlRenderer, convertSideNumberToSide } from '@oare/oare';
import { formatLineNumber } from '@oare/oare/src/tabletUtils';
import DictionaryWord from '@/views/DictionaryWord/index.vue';
import ConnectSpellingOccurrence from './ConnectSpellingOccurrence.vue';
import DiscoursePropertiesCard from './DiscoursePropertiesCard.vue';
import UtilList from '@/components/UtilList/index.vue';
import sl from '@/serviceLocator';
import i18n from '@/i18n';

export default defineComponent({
  props: {
    discourseUnits: {
      type: Array as PropType<DiscourseUnit[]>,
      required: true,
    },
    textUuid: {
      type: String,
      required: false,
    },
    disableEditing: {
      type: Boolean,
      default: false,
    },
    commentMode: {
      type: Boolean,
      default: false,
    },
    discourseToHighlight: {
      type: String,
      required: false,
    },
  },
  components: {
    DiscoursePropertiesCard,
    DictionaryWord,
    ConnectSpellingOccurrence,
    UtilList,
  },
  setup({ discourseUnits, textUuid, disableEditing, discourseToHighlight }) {
    const discourseRenderer = new DiscourseHtmlRenderer(
      discourseUnits,
      i18n.locale as LocaleCode
    );
    const server = sl.get('serverProxy');
    const editingUuid = ref('');
    const inputTranslation = ref('');
    const store = sl.get('store');
    const actions = sl.get('globalActions');
    const loading = ref(false);
    const viewingDialog = ref(false);
    const discourseWordInfo = ref<Word | null>(null);
    const viewingConnectSpellingDialog = ref(false);
    const connectSpellingDialogSpelling = ref('');
    const connectSpellingDialogDiscourseUuid = ref('');
    const isCommenting = ref(false);
    const commentDialogUuid = ref('');
    const commentDialogItem = ref('');

    const allowEditing = computed(
      () => !disableEditing && store.hasPermission('EDIT_TRANSLATION')
    );

    const canConnectSpellings = computed(() =>
      store.hasPermission('CONNECT_SPELLING')
    );

    const discourseReading = (discourse: DiscourseUnit) => {
      let reading;
      if (
        (discourse.type === 'discourseUnit' || discourse.type === 'sentence') &&
        discourse.translation
      ) {
        reading = discourse.translation;
      } else if (discourse.type === 'paragraph' && discourse.paragraphLabel) {
        reading = `<strong><em>${discourse.paragraphLabel}</em></strong>`;
      } else if (
        (discourse.type === 'clause' || discourse.type === 'phrase') &&
        discourse.paragraphLabel
      ) {
        reading = `<em>${discourse.paragraphLabel}</em>`;
      } else if (
        (discourse.type === 'word' || discourse.type === 'number') &&
        discourse.transcription &&
        discourse.explicitSpelling
      ) {
        reading = `${discourse.transcription} (${discourse.explicitSpelling})`;
      } else {
        reading = discourse.explicitSpelling;
      }

      return reading || '';
    };

    const startEdit = (discourse: DiscourseUnit) => {
      editingUuid.value = discourse.uuid || '';
      if (discourse.translation) {
        inputTranslation.value = discourse.translation || '';
      } else {
        inputTranslation.value = discourse.explicitSpelling || '';
      }
    };

    const editLoading = ref(false);
    const discourseEdit = async (discourse: DiscourseUnit) => {
      try {
        if (!textUuid) {
          throw new Error('No text uuid');
        }
        if (discourse.translation) {
          await server.updateDiscourseTranslation(
            discourse.uuid,
            inputTranslation.value,
            textUuid
          );
        } else {
          await server.createDiscourseTranslation(
            discourse.uuid,
            inputTranslation.value,
            textUuid
          );
        }
        editLoading.value = true;
      } catch (err) {
        actions.showErrorSnackbar(
          'Failed to update translations. Please try again.',
          err as Error
        );
      } finally {
        discourse.translation = inputTranslation.value;
        editingUuid.value = '';
        editLoading.value = false;
      }
    };

    const articulateDiscourseHierarchy = ref(false);

    const discourseSelections = ref<DiscourseUnit[]>([]);

    const canSelectDiscourseUnit = (unit: DiscourseUnit): boolean => {
      if (discourseSelections.value.length === 0) {
        return true;
      }
      if (discourseSelections.value[0].parentUuid !== unit.parentUuid) {
        return false;
      }
      const childNumArray = discourseSelections.value.map(
        selection => selection.childNum!
      );
      const minimumChildNumInText = Math.min(...childNumArray);
      const maximumChildNumInText = Math.max(...childNumArray);
      if (
        unit.childNum === minimumChildNumInText - 1 ||
        unit.childNum === maximumChildNumInText + 1 ||
        unit.childNum === minimumChildNumInText ||
        unit.childNum === maximumChildNumInText
      ) {
        return true;
      }
      return false;
    };

    const showCheckbox = (unit: DiscourseUnit): boolean => {
      if (unit.type === 'discourseUnit') {
        return false;
      }

      if (
        unit.type === 'region' &&
        unit.explicitSpelling?.includes('uninscribed')
      ) {
        return false;
      }

      if (unit.type === 'region' && unit.explicitSpelling?.includes('ruling')) {
        return false;
      }

      return true;
    };

    const formatWord = (word: DiscourseDisplayUnit) => {
      const isWordToHighlight =
        discourseToHighlight && word.uuid
          ? discourseToHighlight.includes(word.uuid)
          : false;
      return isWordToHighlight
        ? ` <mark>${word.display}</mark>`
        : ` ${word.display}`;
    };

    watch(articulateDiscourseHierarchy, () => {
      if (!articulateDiscourseHierarchy.value) {
        discourseSelections.value = [];
      }
    });

    const insertParentDiscourseDialog = ref(false);

    const insertParentDiscourseKey = ref(false);
    watch(insertParentDiscourseDialog, () => {
      if (insertParentDiscourseDialog.value) {
        insertParentDiscourseKey.value = !insertParentDiscourseKey.value;
      } else {
        discourseSelections.value = [];
        articulateDiscourseHierarchy.value = false;
      }
    });

    const canInsertParentDiscourse = computed(
      () => textUuid && store.hasPermission('INSERT_PARENT_DISCOURSE_ROWS')
    );

    const insertParentDiscourseComponent = computed(() =>
      textUuid && canInsertParentDiscourse.value
        ? () => import('./InsertParentDiscourseDialog.vue')
        : null
    );

    const openDialog = async (discourseUuid: string, discourseType: string) => {
      try {
        if (discourseType != 'word') {
          return;
        } else {
          loading.value = true;
          actions.showSnackbar('Fetching discourse information...');

          if (discourseUuid) {
            discourseWordInfo.value = await server.getDictionaryInfoByDiscourseUuid(
              discourseUuid
            );
          } else {
            discourseWordInfo.value = null;
          }
          actions.closeSnackbar();
          if (discourseWordInfo.value) {
            viewingDialog.value = true;
          } else if (canConnectSpellings.value && discourseUuid) {
            await openConnectSpellingDialog(discourseUuid);
          } else {
            actions.showSnackbar(
              'No information exists for this text discourse word'
            );
          }
        }
      } catch (err) {
        actions.showErrorSnackbar(
          'Failed to retrieve text discourse word info',
          err as Error
        );
      } finally {
        loading.value = false;
      }
    };

    const openConnectSpellingDialog = async (discourseUuid: string) => {
      try {
        const { spelling } = await server.getSpellingByDiscourseUuid(
          discourseUuid
        );
        viewingConnectSpellingDialog.value = true;
        connectSpellingDialogSpelling.value = spelling;
        connectSpellingDialogDiscourseUuid.value = discourseUuid;
      } catch (err) {
        actions.showErrorSnackbar(
          'Failed to load connect spelling view',
          err as Error
        );
      }
    };

    const closeConnectSpellingDialog = async () => {
      try {
        viewingConnectSpellingDialog.value = false;
        connectSpellingDialogSpelling.value = '';
        connectSpellingDialogDiscourseUuid.value = '';
      } catch (err) {
        actions.showErrorSnackbar(
          'Failed to close connect spelling view',
          err as Error
        );
      }
    };

    const canComment = computed(() => store.hasPermission('ADD_COMMENTS'));

    const openComment = (uuid: string, item: string) => {
      commentDialogUuid.value = uuid;
      commentDialogItem.value = item;
      isCommenting.value = true;
    };

    // To avoid circular dependencies
    const commentComponent = computed(() =>
      canComment.value
        ? () => import('@/components/CommentItemDisplay/index.vue')
        : null
    );

    return {
      discourseRenderer,
      discourseReading,
      startEdit,
      discourseEdit,
      formatLineNumber,
      editingUuid,
      inputTranslation,
      allowEditing,
      editLoading,
      articulateDiscourseHierarchy,
      discourseSelections,
      canSelectDiscourseUnit,
      insertParentDiscourseDialog,
      insertParentDiscourseKey,
      canInsertParentDiscourse,
      insertParentDiscourseComponent,
      openDialog,
      loading,
      discourseWordInfo,
      viewingDialog,
      viewingConnectSpellingDialog,
      connectSpellingDialogSpelling,
      connectSpellingDialogDiscourseUuid,
      closeConnectSpellingDialog,
      convertSideNumberToSide,
      server,
      showCheckbox,
      canComment,
      openComment,
      commentDialogItem,
      commentDialogUuid,
      commentComponent,
      isCommenting,
      formatWord,
    };
  },
});
